const memory = {
  page: {
    eyebrow: 'Enterprise Marketing Memory',
    title: 'Marketing Memory',
    intro: 'This is not a file archive. It shows how Navigator currently understands this company, so diagnosis, action, publishing and review can continue from the same shared understanding.',
    updated: 'Company memory updated',
    lastUpdated: 'Last updated · {date}',
    reportIssue: 'Something looks wrong',
  },
  tabs: {
    overview: 'Current view',
    cognition: 'Enterprise cognition',
    knowledge: 'Knowledge',
    strategy: 'Plan',
    identity: 'Identity',
    connections: 'Connections',
  },
  cognition: {
    title: 'How Navigator came to understand this enterprise', body: 'Return to the confirmed initial profile and the full enterprise research. These are durable project assets, not a one-time onboarding screen.', assets: 'saved cognition assets',
    loading: 'Reading enterprise cognition', loadingBody: 'Retrieving the confirmed profile and enterprise research for this project.', error: 'Enterprise cognition is temporarily unavailable', errorBody: 'The rest of enterprise memory is unchanged. Try reading these two saved assets again.',
    profileEyebrow: 'Initial profile', profileTitle: 'Confirmed enterprise profile', researchEyebrow: 'Enterprise research', researchTitle: 'Research synthesis', notCreated: 'Not created', completed: 'Completed', confirmedAt: 'Confirmed · {date}', generatedAt: 'Generated · {date}', profileEmpty: 'No confirmed initial profile is available for this project.', researchEmpty: 'Enterprise research has not been completed for this project.', summary: 'Research conclusion', facts: '{count} facts', hypotheses: '{count} hypotheses', unknowns: '{count} unknowns', candidates: '{count} candidates', findings: 'Open {count} decision-useful findings', evidence: 'Review facts, hypotheses and unknowns', knownFacts: 'Known facts', hypothesisTitle: 'Hypotheses', unknownTitle: 'Unknowns and conflicts',
    fields: { brand_identity: 'Brand identity', domain_footprint: 'Website and domains', product_lines: 'Products and product lines', markets_languages: 'Markets and languages', buyer_types: 'Typical buyers', competitors: 'Initial competitor candidates', public_content_foundation: 'Public content foundation', website_shape: 'Website status', company_type: 'Initial enterprise type' },
  },
  common: {
    confirmed: 'Confirmed',
    awaitingConfirmation: 'Awaiting confirmation',
    awaitingEvidence: 'More evidence needed',
    continueObserving: 'Keep observing',
    inProgress: 'In progress',
    connected: 'Connected',
    view: 'View',
    close: 'Close',
    cancel: 'Cancel',
    retry: 'Try again',
    later: 'Later',
  },
  contentLanguage: {
    original: 'Original · {language}',
    unchangedHint: 'Company facts, brand originals and source evidence remain in their original language.',
  },
  overview: {
    currentUnderstanding: 'How Navigator currently understands {company} · Awaiting your confirmation',
    viewBrandDirection: 'Review brand direction',
    suggestCorrection: 'Suggest a correction',
    sharedUnderstanding: 'A shared understanding behind every marketing action',
    map: {
      knowledge: 'Knowledge',
      knowledgeHint: 'Facts and market signals',
      strategy: 'Plan',
      strategyHint: 'Strategy and execution',
      identity: 'Identity',
      identityHint: 'Value and choices',
      connections: 'Connections',
      connectionsHint: 'Actions and outcomes',
    },
    cards: {
      knowledgeTitle: 'Knowledge',
      knowledgeBody: 'Review confirmed facts, market response and what still needs to be learned.',
      strategyTitle: 'Plan',
      strategyBody: 'Review this stage’s direction, execution progress and next recommendations.',
      identityTitle: 'Identity',
      identityBody: 'Confirm the core message, value pillars and module roles the market should remember.',
      connectionsTitle: 'Connections',
      connectionsBody: 'See where each outcome came from, what it changed and how it shapes the next step.',
      enter: 'Open →',
      confirmedModules: '{count} messages confirmed',
      liveOutcomes: '{count} outcomes',
    },
    learning: {
      eyebrow: 'Recent learning',
      title: 'What Navigator learned recently',
      body: 'Navigator remembers which messages worked and which directions still need validation, so the next round can build on real results.',
      adopted: 'In use',
      observing: 'Still validating',
    },
  },
  knowledge: {
    eyebrow: 'Knowledge',
    title: 'What is confirmed, and what still needs to be learned',
    body: 'Review product facts, market feedback and missing information, and correct Navigator whenever needed.',
    confirmedFacts: '{count} confirmed public facts',
    search: 'Search facts, market signals or gaps',
    filter: {
      all: 'All',
      facts: 'Confirmed facts',
      market: 'Market signals',
      gaps: 'Knowledge gaps',
      outcomes: 'Outcomes',
    },
    addMaterial: 'Add material',
    category: {
      positioning: 'Product positioning',
      specification: 'Product specification',
      platforms: 'Platforms and version',
      marketSignal: 'Market signal',
      gap: 'Knowledge gap',
      outcome: 'Outcome',
    },
    source: {
      official: 'Official website',
      aiMonitoring: 'AI answer monitoring',
      awaitingSamples: 'Awaiting more samples',
      humanReview: 'Human review',
    },
    action: {
      viewBasis: 'View source',
      viewFacts: 'View {count} source facts',
      viewAnswer: 'View this answer',
      viewMissing: 'See what is missing',
      viewLearning: 'See what was learned',
    },
    status: {
      confirmed: 'Confirmed',
      moreEvidence: 'More evidence needed',
      passedReviews: '{count} reviews passed',
    },
    noMatchTitle: 'No matching memory found',
    noMatchBody: 'Try another keyword or show all types.',
  },
  strategy: {
    eyebrow: 'This stage',
    title: 'How marketing will move forward in this stage',
    body: 'Current goals, active work, items that need your participation and next recommendations are kept together here.',
    responsibilitySummary: '{owned} led by Navigator / {collaborative} collaborative',
    directionAwaiting: 'Direction for this stage · Awaiting your confirmation',
    emptyTitle: 'No stage direction has been confirmed yet',
    emptyBody: 'Navigator will not turn an execution record into a strategy. Confirm the stage goal and direction first.',
    workEmptyTitle: 'No Navigator-led work has been scheduled',
    workEmptyBody: 'Work will appear here after a strategy is confirmed and turned into an execution plan.',
    guidanceEmptyTitle: 'No team guidance has been prepared',
    guidanceEmptyBody: 'Guidance appears only when the strategy requires work outside Navigator’s execution boundary.',
    brief: {
      goal: 'What this stage needs to achieve',
      message: 'What the market should remember',
      reviewWhen: 'When to review the direction',
    },
    journey: {
      goal: 'Set the goal',
      goalHint: 'Who to influence and what to change',
      theme: 'Choose the theme',
      themeHint: 'Give every message one shared focus',
      work: 'Move the work forward',
      workHint: 'Content, pages, publishing and collaboration',
      feedback: 'Read the feedback',
      feedbackHint: 'AI answers, leads and customer voice',
      next: 'Decide what comes next',
      nextHint: 'Continue, adjust or change direction',
    },
    navigatorLed: {
      eyebrow: 'Led by Navigator',
      title: 'Navigator will handle these',
      body: 'Navigator carries the following work from direction-setting through publishing and review.',
      count: '{count} workstreams',
      positioningTitle: 'Define this stage’s proposition and competitive position',
      positioningBody: 'Start with what buyers truly care about, then clarify the core value, module roles and reasons to choose.',
      contentTitle: 'Turn strategy into publishable content',
      contentBody: 'Produce product pages, FAQs, buyer guides, comparisons and multilingual pages.',
      machineTitle: 'Help both the website and AI understand new content',
      machineBody: 'Add discoverable paths, page relationships and machine-readable information to the existing website.',
      publishTitle: 'Publish and confirm every outcome is visible',
      publishBody: 'Use manual, assisted or automated publishing within your authorization, then check pages and search entry points.',
      reviewTitle: 'Track market feedback and prepare the next stage',
      reviewBody: 'Use AI answers, leads and customer feedback to decide what continues and what changes.',
      completed: 'Completed',
      pagesOnline: '{count} pages live',
      ongoing: 'Ongoing',
      connected: 'Connected',
      monitoring: 'Monitoring',
    },
    collaboration: {
      eyebrow: 'With your team',
      title: 'These need you or your team',
      body: 'Navigator prepares direction and content. You decide when to start, and the relevant team completes the work.',
      socialTitle: '{channels} publishing',
      socialBody: 'Navigator prepares themes, cadence and adaptations; you review and publish.',
      socialStatus: 'Together',
      mediaTitle: 'Paid media',
      mediaBody: 'Navigator recommends audiences, topics and creative; your media team manages budget and placement.',
      mediaStatus: 'Account needed',
      prTitle: 'PR, events and partnerships',
      prBody: 'Navigator recommends topics, timing and partnership directions; you or your partners handle outreach.',
      prStatus: 'Team needed',
    },
    results: {
      eyebrow: 'What the market says',
      title: 'Real feedback decides what comes next',
      body: 'Navigator watches how the market talks about you, which buyers are approaching and why customers choose or hesitate.',
      aiTitle: 'How AI is talking about you',
      aiBody: 'Track whether the brand is mentioned, how it is recommended, which competitors appear beside it and what sources are cited.',
      aiAction: 'Ongoing monitoring · View latest evaluation →',
      leadsTitle: 'Which customers are approaching',
      leadsBody: 'Combine lead source, buyer questions and follow-up progress to judge whether marketing is bringing the right people.',
      leadsAction: 'Leads connected · View changes →',
      surveyTitle: 'Why customers choose or hesitate',
      surveyBody: 'Ask which messages built trust, what remains unclear and what shaped the final decision.',
      surveyAction: 'Available anytime · Create a survey →',
    },
    recommendation: {
      continue: 'Recommended: continue',
      adjust: 'Recommended: adjust',
      schedule: 'Awaiting scheduling',
    },
    methods: {
      eyebrow: 'What Navigator has learned',
      title: 'Working principles Navigator has learned',
      body: 'These principles come from real work and human judgment, and continue to shape the next strategy and content.',
      count: '{active} in use / {observing} still validating',
      search: 'Search principles',
      filterAll: 'All',
      filterActive: 'In use',
      filterObserving: 'Still validating',
      expression: 'Expression principle',
      competition: 'Competition principle',
      promise: 'Promise principle',
      inUse: 'In use',
      validating: 'Still validating',
      impact: 'See how it changes the work',
      scope: 'View where it applies',
      validation: 'See what still needs validation',
      emptyBody: 'No working principle has enough evidence to appear here yet.',
    },
  },
  identity: {
    eyebrow: 'How the market should remember you',
    title: 'How {company} wants to be understood',
    body: 'Confirm what the market should remember first, and how the company’s products and capabilities support that shared understanding.',
    confirmedModules: '{count} module messages confirmed',
    coreAwaiting: 'Core message · Awaiting your confirmation',
    confirm: 'Confirm this message',
    revise: 'Suggest a revision',
    usage: 'See where it will be used',
    awaitingDecision: 'Awaiting your decision',
    notActive: 'Core message is not active yet',
    afterConfirm: 'Once confirmed, Navigator will use it across the website, content and future marketing actions.',
    pillarAwaiting: 'Please confirm',
    modulesEyebrow: 'Confirmed module messages',
    modulesTitle: 'How products and capabilities support the core value',
    modulesCount: '{count} confirmed',
    moduleAction: 'Confirmed · View full message',
    noCoreTitle: 'The core message has not been formed yet',
    noCoreBody: 'Navigator needs confirmed company material and human intent before proposing a core message.',
    noModulesBody: 'Confirmed product and capability messages will appear here.',
  },
  connections: {
    eyebrow: 'From one stage to the next',
    title: 'Every action makes the next one more precise',
    body: 'See what happened in the previous stage, how the market responded and how those results change the next step.',
    onlineOutcomes: '{count} live outcomes',
    journeyEyebrow: 'One continuous journey',
    journeyTitle: 'From judgment to execution, then let results shape the next stage',
    steps: {
      stage: 'Understand the stage',
      stageHint: 'Business goal, buyer questions and perception gaps',
      strategy: 'Set the strategy',
      strategyHint: 'Theme, competitive position and success criteria',
      work: 'Move the work forward',
      workHint: 'Organize content, pages, publishing and collaboration',
      results: 'Read the results',
      resultsHint: 'AI answers, leads and customer surveys',
      next: 'Prepare the next stage',
      nextHint: 'Continue, adjust or change direction and retain the learning',
    },
    note: 'Navigator considers AI answers, leads and customer feedback together. Effective directions continue, and attempts that did not work are remembered so the next stage avoids the same detour.',
    recentTitle: 'Most recent complete action',
    recentBody: 'From a buyer question to a bilingual guide, then from launch results into the next round of content.',
    noRecentBody: 'No complete action lineage has been recorded yet.',
    viewProcess: 'View the full process',
    accumulatedTitle: 'What Navigator has accumulated',
    accumulatedBody: 'These records let future work continue from existing experience instead of starting over.',
    stats: {
      live: 'Live outcomes',
      attempts: 'Recorded attempts',
      judgments: 'Learning decisions',
      actions: 'Complete actions',
    },
  },
  airlockGap: {
    eyebrow: 'From Airlock release',
    title: 'Complete the business evidence Airlock needs',
    subject: 'Subject to complete',
    status: 'Current status',
    statusPending: 'Waiting for material',
    statusQueued: 'Material submitted · awaiting processing',
    statusSynced: 'Ready to return and test',
    flowLabel: 'Knowledge completion flow',
    flow: {
      supply: 'Provide the fact',
      classify: 'Classify the material',
      confirm: 'Confirm knowledge',
      retest: 'Return and retest',
    },
    reason: {
      missing_knowledge: 'Approved company material does not yet contain a reliable answer for this subject. Add the source fact or an accurate business explanation below.',
      conflicting_knowledge: 'Existing sources disagree on this subject. Add the current authoritative position and identify what it replaces.',
      knowledge_not_public: 'The information exists, but Airlock is not yet permitted to use it publicly. Add a publishable statement or return to choose a safe boundary.',
      knowledge_not_synced: 'Confirmed knowledge has not yet reached this Airlock scope. Review the knowledge record or add the missing source here.',
      not_applicable_review: 'This subject still needs a business decision. Add the applicable fact, or return to Airlock and confirm that it does not apply.',
    },
    queuedBody: 'Your statement is now in the material-processing queue. The release gap remains open until it becomes confirmed knowledge and is synchronized to this Airlock.',
    addStatement: 'Add a factual statement',
    submitted: 'Statement submitted',
    reviewKnowledge: 'Review existing knowledge',
    returnToAirlock: 'Return to Airlock release',
    correctionTitle: 'Add evidence for this Airlock gap',
    correctionBody: 'State the verifiable business fact and, where possible, name its source. Submission does not publish it or close the gap automatically.',
    correctionLabel: 'Business fact or evidence',
    correctionPlaceholder: 'For example: The minimum order is 500 units for the standard specification. Source: the current 2026 sales policy…',
    correctionSubmit: 'Submit for processing',
    queuedToast: 'Material received. The Airlock gap stays open until knowledge is confirmed and synchronized.',
  },
  states: {
    loadingTitle: 'Bringing company memory together',
    loadingBody: 'Navigator is reading facts, strategy, execution, outcomes and learning. Missing data will not be shown as zero or treated as absent.',
    emptyEyebrow: 'New company',
    emptyTitle: 'This company’s memory has not started yet',
    emptyBody: 'Connect the website and source material first. Navigator will do the initial research and bring back only the decisions that need you.',
    connectMaterial: 'Connect first material',
    readWebsite: 'Read the website first',
    errorEyebrow: 'Read failed',
    errorTitle: 'Company memory is temporarily unavailable',
    errorBody: 'Existing memory is safe and will not be replaced by an empty state. Try again or review the last trusted snapshot.',
    retry: 'Try again',
    lastSnapshot: 'View last trusted snapshot',
    snapshotUnavailable: 'No trusted snapshot is available in this session.',
    humanEyebrow: 'Your decision is needed',
    humanTitle: 'Navigator found more than one valid direction',
    humanBody: 'It will not choose on your behalf. Compare the options and confirm the one that best reflects the company’s intent.',
    choose: 'Review options',
    later: 'Decide later',
    decisionDeferred: 'The decision remains pending and no direction was changed.',
  },
  detail: {
    eyebrow: 'Memory detail',
    correct: 'This needs correcting',
    source: 'View source',
    correctionTitle: 'Correct Navigator’s understanding',
    correctionBody: 'Your correction becomes a new input. It will not silently overwrite confirmed facts or the original source.',
    correctionLabel: 'Your correction',
    correctionPlaceholder: 'For example: Buyers choose us for long-term control, not simply because one feature is more convenient…',
    cancel: 'Cancel',
    submit: 'Submit correction',
    submitting: 'Submitting…',
    correctionQueued: 'Correction received. It is awaiting domain classification before entering company memory.',
    correctionFailed: 'The correction could not be submitted.',
    fields: {
      id: 'Record ID',
      locale: 'Original language',
      source: 'Source',
      scope: 'Scope',
      confidence: 'Confidence',
      status: 'Status',
      question: 'Buyer question',
      platform: 'Platform',
      output: 'Related output',
    },
  },
} as const

export default memory
